import { LoaderFunction } from 'react-router-dom';

import { getMyLearning, getRecentlyViewedLessons, getArchivedContent } from 'src/services/http/myLearning';
import { MyLearningPageContentSections } from 'src/pages/MyLearningPage/constants';
import { MyLearningLoaderProps } from 'src/pages/MyLearningPage/types';
import { getCoursesAndLearningPathsTitleAndSlugs } from 'src/pages/MyLearningPage/utils/dataTransformers';

/**
 * Loader for the on-demand tab.
 * Fetches all necessary data for courses, learning paths, and lessons.
 */
export const onDemandLoader: LoaderFunction = async (): Promise<MyLearningLoaderProps> => {
  // Fetch all data in parallel for better performance
  const [recentlyViewedLessonsData, retiredContentData, inProgressData, completedData, savedData] = await Promise.all([
    getRecentlyViewedLessons(),
    getArchivedContent(),
    getMyLearning(MyLearningPageContentSections.IN_PROGRESS),
    getMyLearning(MyLearningPageContentSections.COMPLETE),
    getMyLearning(MyLearningPageContentSections.SAVED),
  ]);

  // Track saved content IDs
  const savedContentfulIds = new Set([
    ...(savedData?.learningPath?.map((item) => item.contentfulId) ?? []),
    ...(savedData?.courses?.map((item) => item.contentfulId) ?? []),
  ]);

  // Use Sets for O(1) lookup performance instead of O(n) with includes()
  const inProgressContentfulIds = new Set([
    ...(inProgressData?.learningPath?.map((item) => item.contentfulId) ?? []),
    ...(inProgressData?.courses?.map((item) => item.contentfulId) ?? []),
  ]);
  const completedContentfulIds = new Set([
    ...(completedData?.learningPath?.map((item) => item.contentfulId) ?? []),
    ...(completedData?.courses?.map((item) => item.contentfulId) ?? []),
  ]);

  // Saved items that are not in in-progress or completed
  const nonStartedSavedLearningData = {
    learningPath:
      savedData?.learningPath?.filter(
        (item) => !inProgressContentfulIds.has(item.contentfulId) && !completedContentfulIds.has(item.contentfulId),
      ) ?? [],
    courses:
      savedData?.courses?.filter(
        (item) => !inProgressContentfulIds.has(item.contentfulId) && !completedContentfulIds.has(item.contentfulId),
      ) ?? [],
  };

  const combinedMyLearningData = {
    learningPath: [
      ...(inProgressData?.learningPath ?? []),
      ...(completedData?.learningPath ?? []),
      ...(nonStartedSavedLearningData.learningPath ?? []),
    ],
    courses: [
      ...(inProgressData?.courses ?? []),
      ...(completedData?.courses ?? []),
      ...(nonStartedSavedLearningData.courses ?? []),
    ],
  };

  const mappedData = await getCoursesAndLearningPathsTitleAndSlugs(
    recentlyViewedLessonsData,
    combinedMyLearningData,
    retiredContentData,
    savedContentfulIds,
  );

  return {
    ...mappedData,
    areUnseenNotificationsAvailable: false,
  };
};
